/**
 * errorLogger.js
 *
 * Offline-first frontend error capture with localStorage queue and backend sync.
 *
 * - Captures JS errors, unhandled promise rejections, Vue component errors
 * - Stores errors locally (localStorage) when offline or before sync
 * - Syncs queue to backend on app load and on reconnection
 * - Uses UUID as primary key, generated device-side
 */

const QUEUE_KEY = 'vect_error_queue';
const MAX_QUEUE  = 200;  // FIFO evict oldest when full
const DEVICE_KEY = 'vect_device_id';

/** Stable device fingerprint stored in localStorage */
function getDeviceId() {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = 'dev-' + Math.random().toString(36).slice(2, 10) + '-' + Date.now().toString(36);
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

/** Simple UUID v4 generator (no dependency) */
function uuidv4() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function readQueue() {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
  } catch {
    return [];
  }
}

function writeQueue(queue) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  } catch {
    // localStorage full — evict oldest half
    try {
      const half = queue.slice(Math.floor(queue.length / 2));
      localStorage.setItem(QUEUE_KEY, JSON.stringify(half));
    } catch { /* give up */ }
  }
}

let _store = null;   // Vuex store reference, set by init()
let _apiBase = null; // e.g. 'https://api.example.com/api'
let _syncInProgress = false;

export function init(store, apiBase) {
  _store = store;
  _apiBase = apiBase;
}

/**
 * Build a rich context object for the error entry.
 */
function buildContext(extra = {}) {
  return {
    user_agent: navigator.userAgent,
    url:        window.location.href,
    route:      window.location.pathname,
    app_version: window.APP_VERSION || 'unknown',
    ...extra,
  };
}

/**
 * Core capture function — call this from all error handlers.
 *
 * @param {string} message   Human-readable error message
 * @param {string|null} stack  Stack trace string
 * @param {object} options   { level, action, context, url }
 */
export function logError(message, stack = null, options = {}) {
  try {
    const userId = _store?.state?.user?.id ?? null;
    const entry = {
      id:          uuidv4(),
      user_id:     userId,
      device_id:   getDeviceId(),
      source:      'frontend',
      level:       options.level || 'error',
      message:     String(message).slice(0, 1000),
      stack:       stack ? String(stack).slice(0, 8000) : null,
      url:         options.url || window.location.href,
      action:      options.action || null,
      context:     buildContext(options.context || {}),
      occurred_at: new Date().toISOString(),
    };

    const queue = readQueue();
    queue.push(entry);

    // FIFO eviction when over limit
    if (queue.length > MAX_QUEUE) queue.splice(0, queue.length - MAX_QUEUE);

    writeQueue(queue);

    // Attempt immediate sync (non-blocking)
    syncQueue().catch(() => { /* silent — will retry later */ });
  } catch {
    /* never throw from error handler */
  }
}

/**
 * Sync all queued errors to the backend.
 * Idempotent: backend deduplicates by UUID.
 */
export async function syncQueue() {
  if (_syncInProgress) return;
  const queue = readQueue();
  if (!queue.length || !_apiBase) return;

  _syncInProgress = true;
  try {
    const token = _store?.state?.token
               || localStorage.getItem('token')
               || sessionStorage.getItem('token');

    const res = await fetch(`${_apiBase}/error-logs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ items: queue }),
    });

    if (res.ok) {
      // Clear only entries we just synced (leave any newly added ones)
      const synced = new Set(queue.map(e => e.id));
      const remaining = readQueue().filter(e => !synced.has(e.id));
      writeQueue(remaining);
    }
  } catch {
    /* offline or server error — will retry on next sync call */
  } finally {
    _syncInProgress = false;
  }
}
